import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { fmtMoney, fmtDate } from "@/lib/format";
import { printMonthlyLoanSummary } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CalendarRange,
  FileText,
  RefreshCw,
  IndianRupee,
  TrendingUp,
  Printer,
} from "lucide-react";
import { toast } from "sonner";

function currentMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export default function LoanMonthlySummary() {
  const { settings, business, currency, dateFormat } = useSettings();

  const [selectedMonth, setSelectedMonth] = useState(currentMonth());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReport = useCallback(
    async (monthValue = selectedMonth) => {
      if (!monthValue || !monthValue.includes("-")) {
        toast.error("Please select a valid month");
        return;
      }

      const [year, month] = monthValue.split("-");

      setLoading(true);

      try {
        const res = await api.get("/reports/monthly-loans", {
          params: {
            year: Number(year),
            month: Number(month),
          },
        });

        setReport(res.data);
      } catch (e) {
        toast.error(
          apiError(e.response?.data?.detail) ||
            "Unable to load monthly loan summary"
        );
      } finally {
        setLoading(false);
      }
    },
    [selectedMonth]
  );

  useEffect(() => {
    loadReport(currentMonth());
  }, []);

  const monthTitle = (() => {
    if (!selectedMonth) return "";

    const [year, month] = selectedMonth.split("-");

    return new Intl.DateTimeFormat("en-IN", {
      month: "long",
      year: "numeric",
    }).format(
      new Date(
        Number(year),
        Number(month) - 1,
        1
      )
    );
  })();

  const MoneyCard = ({
    label,
    value,
    icon: Icon,
    money = true,
  }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">
            {label}
          </div>

          <div className="mt-2 font-mono text-2xl font-bold text-navy">
            {money
              ? fmtMoney(Number(value || 0), currency)
              : Number(value || 0)}
          </div>
        </div>

        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-navy">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="space-y-6"
      data-testid="loan-monthly-summary-page"
    >
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">
          Loan Monthly Summary
        </h1>

        <p className="text-slate-500 mt-1">
          View loans made and expected interest month by month
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="w-full sm:w-64">
            <label className="block text-sm font-medium text-navy mb-2">
              Select Month
            </label>

            <Input
              type="month"
              value={selectedMonth}
              onChange={(e) =>
                setSelectedMonth(e.target.value)
              }
              data-testid="monthly-loan-month"
            />
          </div>

          <Button
            className="bg-navy hover:bg-navy-hover"
            onClick={() => loadReport()}
            disabled={loading}
            data-testid="generate-monthly-loan-report"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${
                loading ? "animate-spin" : ""
              }`}
            />

            {loading ? "Loading…" : "Generate"}
          </Button>
        </div>
      </div>

      {report && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <CalendarRange className="h-4 w-4" />
              <span>
                Showing loans made in{" "}
                <b className="text-navy">
                  {monthTitle}
                </b>
              </span>
            </div>

            <Button
              variant="outline"
              onClick={() =>
                printMonthlyLoanSummary(
                  report,
                  monthTitle,
                  business,
                  settings
                )
              }
              data-testid="print-monthly-loan-summary-button"
            >
              <Printer className="h-4 w-4 mr-2" />
              FinLend Secure Print PDF
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <MoneyCard
              label="Total Loans"
              value={report.total_loans}
              icon={FileText}
              money={false}
            />

            <MoneyCard
              label="New Loans"
              value={report.new_loans}
              icon={FileText}
              money={false}
            />

            <MoneyCard
              label="Renewals"
              value={report.renewal_loans}
              icon={RefreshCw}
              money={false}
            />

            <MoneyCard
              label="Total Money Lent"
              value={report.total_money_lent}
              icon={IndianRupee}
            />

            <MoneyCard
              label="Total Interest"
              value={report.total_interest}
              icon={TrendingUp}
            />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="font-heading font-bold text-lg text-navy">
                Loan Details
              </h2>

              <p className="text-sm text-slate-500 mt-1">
                {report.total_loans || 0} loan
                {Number(report.total_loans || 0) === 1
                  ? ""
                  : "s"}{" "}
                recorded
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[950px] text-sm">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500">
                      Sl.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500">
                      Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500">
                      Customer
                    </th>

                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500">
                      Loan No.
                    </th>

                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500">
                      Type
                    </th>

                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500">
                      Principal
                    </th>

                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500">
                      Interest
                    </th>

                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500">
                      Renewal Amount
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {(report.rows || []).map(
                    (row, index) => (
                      <tr
                        key={`${row.loan_number}-${index}`}
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="px-4 py-4 text-slate-500">
                          {index + 1}
                        </td>

                        <td className="px-4 py-4 text-navy whitespace-nowrap">
                          {fmtDate(
                            row.date,
                            dateFormat
                          )}
                        </td>

                        <td className="px-4 py-4 font-medium text-navy">
                          {row.customer_name}
                        </td>

                        <td className="px-4 py-4 font-mono text-navy">
                          {row.loan_number}
                        </td>

                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                              row.type === "Renewal"
                                ? "bg-amber-100 text-amber-800"
                                : "bg-emerald-100 text-emerald-800"
                            }`}
                          >
                            {row.type}
                          </span>
                        </td>

                        <td className="px-4 py-4 text-right font-mono text-navy">
                          {fmtMoney(
                            row.principal,
                            currency
                          )}
                        </td>

                        <td className="px-4 py-4 text-right font-mono text-maroon">
                          {fmtMoney(
                            row.interest,
                            currency
                          )}
                        </td>

                        <td className="px-4 py-4 text-right font-mono text-navy">
                          {row.type === "Renewal"
                            ? fmtMoney(
                                row.renewal_amount,
                                currency
                              )
                            : "—"}
                        </td>
                      </tr>
                    )
                  )}

                  {(report.rows || []).length ===
                    0 && (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-5 py-14 text-center text-slate-400"
                      >
                        No loans were made in{" "}
                        {monthTitle}.
                      </td>
                    </tr>
                  )}
                </tbody>

                {(report.rows || []).length > 0 && (
                  <tfoot className="bg-slate-50 border-t border-slate-200">
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-4 font-bold text-navy"
                      >
                        MONTH TOTAL
                      </td>

                      <td className="px-4 py-4 text-right font-mono font-bold text-navy">
                        {fmtMoney(
                          (report.rows || []).reduce(
                            (sum, row) =>
                              sum + Number(row.principal || 0),
                            0
                          ),
                          currency
                        )}
                      </td>

                      <td className="px-4 py-4 text-right font-mono font-bold text-maroon">
                        {fmtMoney(
                          report.total_interest,
                          currency
                        )}
                      </td>

                      <td className="px-4 py-4 text-right font-mono font-bold text-navy">
                        {fmtMoney(
                          (report.rows || [])
                            .filter(
                              (row) =>
                                row.type ===
                                "Renewal"
                            )
                            .reduce(
                              (sum, row) =>
                                sum +
                                Number(
                                  row.renewal_amount ||
                                    0
                                ),
                              0
                            ),
                          currency
                        )}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          <div className="text-xs text-slate-400">
            Renewal interest shows only the new
            interest generated by the renewed loan.
            Renewal Amount shows only fresh cash given
            to the customer.
          </div>
        </>
      )}
    </div>
  );
}
